import {createContext, useCallback, useContext, useSyncExternalStore} from "react";
import {connectionAttemptZodValidator, type DAG_ConnectionAttempt} from "~/domainModels/dag/dataSourceConnectionAttempt";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {jsonZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

const CONNECTION_ATTEMPT_KEY_PREFIX = "connectionAttempt/";

type Listener = () => void;

export class ConnectionAttemptClient {
    private connectionAttempts: Map<Uuid, DAG_ConnectionAttempt>;

    // Global listeners (for useConnectionAttempts - all items)
    private globalListeners: Set<Listener> = new Set();

    // Per-ID listeners (for useConnectionAttempt - single item)
    private itemListeners: Map<Uuid, Set<Listener>> = new Map();

    // Cached array for snapshot stability (useSyncExternalStore needs stable references)
    private cachedAttemptsList: DAG_ConnectionAttempt[] = [];

    constructor() {
        this.connectionAttempts = new Map();

        // Load all connectionAttempt/* keys from localStorage
        this.reloadFromLocalStorage();

        // Cross-tab sync: listen for changes from other tabs
        if (typeof window !== "undefined") {
            window.addEventListener("storage", this.handleStorageEvent);
        }
    }

    // Clean up when no longer needed
    destroy(): void {
        if (typeof window !== "undefined") {
            window.removeEventListener("storage", this.handleStorageEvent);
        }
    }

    private handleStorageEvent = (event: StorageEvent): void => {
        if (event.key === null) {
            // Storage was cleared
            this.reloadFromLocalStorage();
            this.notifyGlobal();
            return;
        }

        if (!event.key.startsWith(CONNECTION_ATTEMPT_KEY_PREFIX)) {
            return;
        }

        const id = event.key.slice(CONNECTION_ATTEMPT_KEY_PREFIX.length) as Uuid;

        if (event.newValue === null) {
            // Item was deleted
            this.connectionAttempts.delete(id);
        } else {
            // Item was added/updated
            const result = jsonZodValidator.pipe(connectionAttemptZodValidator).safeParse(event.newValue);
            if (result.success) {
                this.connectionAttempts.set(id, result.data);
            }
        }

        this.rebuildCache();
        this.notifyItem(id);
        this.notifyGlobal();
    };

    // Reload all connection attempts from localStorage
    private reloadFromLocalStorage(): void {
        this.connectionAttempts.clear();

        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key === null || !key.startsWith(CONNECTION_ATTEMPT_KEY_PREFIX)) {
                    continue;
                }

                const value = localStorage.getItem(key);
                if (value === null) {
                    continue;
                }

                const result = jsonZodValidator.pipe(connectionAttemptZodValidator).safeParse(value);
                if (result.success) {
                    this.connectionAttempts.set(result.data.id, result.data);
                }
            }
        } catch (error) {
            console.error("Failed to load from localStorage:", error);
        }

        this.rebuildCache();
    }

    // Reload all connection attempts from localStorage and notify all subscribers
    refreshFromLocalStorage(): void {
        this.reloadFromLocalStorage();

        // Notify all subscribers
        this.notifyGlobal();
        // Also notify per-item subscribers for all loaded items
        for (const id of this.connectionAttempts.keys()) {
            this.notifyItem(id);
        }
    }

    private rebuildCache(): void {
        this.cachedAttemptsList = Array.from(this.connectionAttempts.values());
    }

    // Get a single connection attempt by ID
    getConnectionAttempt(id: Uuid): DAG_ConnectionAttempt | undefined {
        return this.connectionAttempts.get(id);
    }

    // Get all connection attempts (returns cached array for snapshot stability)
    getAllConnectionAttempts(): DAG_ConnectionAttempt[] {
        return this.cachedAttemptsList;
    }

    // Set (create or update) a connection attempt
    setConnectionAttempt(connectionAttempt: DAG_ConnectionAttempt): Result<void> {
        this.connectionAttempts.set(connectionAttempt.id, connectionAttempt);

        try {
            localStorage.setItem(`${CONNECTION_ATTEMPT_KEY_PREFIX}${connectionAttempt.id}`, JSON.stringify(connectionAttempt));
        } catch (error) {
            console.log("Failed to save to localStorage, 5979a27f-807a-49b6-9002-dbbe4ed8389a:", error);
            return errorResult(new Error("Failed to save to localStorage, 5979a27f-807a-49b6-9002-dbbe4ed8389a"));
        }

        this.rebuildCache();
        this.notifyItem(connectionAttempt.id);
        this.notifyGlobal();

        return dataResult(undefined);
    }

    // Delete a connection attempt
    deleteConnectionAttempt(id: Uuid): Result<void> {
        if (!this.connectionAttempts.has(id)) {
            return errorResult(new Error("Connection attempt not found, f02c8601-cf89-4ee5-a87d-7a3302b91b68"));
        }

        this.connectionAttempts.delete(id);

        try {
            localStorage.removeItem(`${CONNECTION_ATTEMPT_KEY_PREFIX}${id}`);
        } catch (error) {
            console.error("Failed to remove from localStorage:", error);
            return errorResult(new Error("Failed to remove from localStorage, 21a2425e-0463-456b-910b-9d86583f501c"));
        }

        this.rebuildCache();
        this.notifyItem(id);
        this.notifyGlobal();

        return dataResult(undefined);
    }

    // Subscribe to ALL changes (for useConnectionAttempts)
    subscribe(listener: Listener): () => void {
        this.globalListeners.add(listener);
        return () => {
            this.globalListeners.delete(listener);
        };
    }

    // Subscribe to a SPECIFIC ID's changes (for useConnectionAttempt)
    subscribeToItem(id: Uuid, listener: Listener): () => void {
        let listeners = this.itemListeners.get(id);
        if (listeners === undefined) {
            listeners = new Set();
            this.itemListeners.set(id, listeners);
        }
        listeners.add(listener);

        return () => {
            listeners.delete(listener);
            // Clean up empty Sets
            if (listeners.size === 0) {
                this.itemListeners.delete(id);
            }
        };
    }

    private notifyGlobal(): void {
        for (const listener of this.globalListeners) {
            listener();
        }
    }

    private notifyItem(id: Uuid): void {
        const listeners = this.itemListeners.get(id);
        if (listeners !== undefined) {
            for (const listener of listeners) {
                listener();
            }
        }
    }
}

type ConnectionAttemptClientContextData = ConnectionAttemptClient;

export const ConnectionAttemptClientContext = createContext<ConnectionAttemptClientContextData | undefined>(undefined);

// Hook to get the client instance
export function useConnectionAttemptClient(): ConnectionAttemptClient {
    const client = useContext(ConnectionAttemptClientContext);
    if (client === undefined) {
        throw new Error("ConnectionAttemptClientContext.Provider not found");
    }
    return client;
}

// Hook to subscribe to all connection attempts
export function useConnectionAttempts(): DAG_ConnectionAttempt[] {
    const client = useConnectionAttemptClient();

    return useSyncExternalStore(
        (callback) => client.subscribe(callback),
        () => client.getAllConnectionAttempts(),
        () => client.getAllConnectionAttempts(),
    );
}

// Hook to subscribe to a single connection attempt by ID (optimized - only re-renders when this specific ID changes)
export function useConnectionAttempt(id: Uuid): DAG_ConnectionAttempt | undefined {
    const client = useConnectionAttemptClient();

    const subscribe = useCallback((callback: () => void) => client.subscribeToItem(id, callback), [client, id]);

    const getSnapshot = useCallback(() => client.getConnectionAttempt(id), [client, id]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
